import { html, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import { LibRadio } from '../radio/lib-radio';
import css from './lib-radio-group.css';

/** Coordinates selection and keyboard navigation across radio shadow roots. */
@customElement('lib-radio-group')
export class LibRadioGroup extends LibElement {
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @property() orientation = 'horizontal';
  private resetPending = false;

  readonly #observer = new MutationObserver(() => this.#syncTabs());

  override connectedCallback(): void {
    super.connectedCallback();
    this.#observer.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked', 'disabled'] });
  }

  override disconnectedCallback(): void {
    this.#observer.disconnect();
    super.disconnectedCallback();
  }

  constructor() {
    super();
    this.addEventListener('change', event => {
      const selected = event.target;
      if (!(selected instanceof LibRadio) || selected.closest('lib-radio-group') !== this) return;
      for (const radio of this.#radios()) radio.setSelected(radio === selected);
      this.#syncTabs();
    });
    this.addEventListener('keydown', event => this.#keydown(event));
    this.addEventListener('focusin', () => this.#syncTabs());
    this.addEventListener('lib-disabled-change', () => this.#syncTabs());
    this.addEventListener('lib-radio-reset', event => {
      if (!(event.target instanceof LibRadio) || event.target.closest('lib-radio-group') !== this) return;
      event.stopPropagation();
      if (this.resetPending) return;
      this.resetPending = true;
      // All form controls must restore their state before the group reports its selection.
      queueMicrotask(() => {
        this.resetPending = false;
        this.#syncTabs();
        this.dispatchEvent(new CustomEvent('lib-radio-reset', {
          bubbles: true, composed: true,
          detail: { value: this.#radios().find(radio => radio.checked)?.value ?? null },
        }));
      });
    });
  }

  override render() {
    return html`<slot @slotchange=${this.#syncTabs}></slot><slot name="message"></slot>`;
  }

  #radios(): LibRadio[] {
    return Array.from(this.querySelectorAll('lib-radio')).filter(radio => radio instanceof LibRadio && radio.closest('lib-radio-group') === this);
  }

  #syncTabs(): void {
    const radios = this.#radios();
    const active = radios.find(radio => radio.checked && !radio.effectivelyDisabled) ?? radios.find(radio => !radio.effectivelyDisabled);
    for (const radio of radios) {
      void radio.updateComplete.then(() => {
        const input = radio.shadowRoot?.querySelector('input');
        if (input) input.tabIndex = radio === active ? 0 : -1;
      });
    }
  }

  #keydown(event: KeyboardEvent): void {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
    const radios = this.#radios().filter(radio => !radio.effectivelyDisabled);
    const current = event.composedPath().find(node => node instanceof LibRadio);
    const index = radios.indexOf(current as LibRadio);
    if (index < 0) return;
    event.preventDefault();
    const direction = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
    const next = radios[(index + direction + radios.length) % radios.length];
    next.select();
    next.shadowRoot?.querySelector('input')?.focus();
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-radio-group': LibRadioGroup; }
}
